import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import {
    COUNTRIES,
    COUNTRY_CODES,
    DEFAULT_COUNTRY,
    defaultLanguageFor,
    getCountry,
    languagesForCountry,
} from "@/i18n/countries";

/**
 * CountryContext — global source of truth for the visitor's current
 * market. Detection cascade (highest priority first):
 *   1. `?country=XX` query param
 *   2. First URL segment when it matches a supported code (/hk/browse)
 *   3. `localStorage.perx_country` (returning visitor)
 *   4. Backend `/api/public/geo` (Cloudflare CF-IPCountry header)
 *   5. DEFAULT_COUNTRY (Hong Kong)
 *
 * The visible country list is intersected with `/api/public/countries`
 * (the ACTIVE set). If the cached/detected country becomes inactive
 * — e.g. admin flips AU off in `/admin/countries` — the visitor is
 * snapped to the default automatically.
 */

const STORAGE_COUNTRY = "perx_country";
const STORAGE_LANG_MANUAL = "perx_lang_manual"; // "1" once user hand-picks a language

const CountryContext = createContext(null);

export function CountryProvider({ children }) {
    const { i18n } = useTranslation();
    const [country, setCountryState] = useState(() => readInitialCountry());
    const [activeCodes, setActiveCodes] = useState(null); // null = not loaded yet
    // Per-country hero video/poster URLs indexed by code. Populated
    // alongside `activeCodes` from `/api/public/countries` so the
    // landing hero can localise to the visitor's market.
    const [heroByCode, setHeroByCode] = useState({});
    // Per-country hero-copy overrides indexed as {code: {lang: {field: value}}}.
    // Populated alongside the active list from `/api/public/countries`.
    // `useLocalizedHero` cascades: DB override → i18n variant → generic.
    const [heroCopyByCode, setHeroCopyByCode] = useState({});
    const [ready, setReady] = useState(false);

    // Boot: fetch active countries + (if we haven't cached anything) a
    // geo hint. Two parallel calls — settle both, then reveal the app.
    useEffect(() => {
        let cancelled = false;
        const geoNeeded = !localStorage.getItem(STORAGE_COUNTRY);

        const jobs = [
            api.get("/public/countries").catch(() => null),
            geoNeeded ? api.get("/public/geo").catch(() => null) : Promise.resolve(null),
        ];

        Promise.all(jobs).then(([countriesRes, geoRes]) => {
            if (cancelled) return;

            // Active countries — fall back to [HK] if the call failed.
            const activeList = countriesRes?.data?.countries || [];
            const active = activeList.length
                ? new Set(activeList.map((c) => c.code))
                : new Set([DEFAULT_COUNTRY]);
            setActiveCodes(active);
            // Cache per-country hero URLs from the same payload.
            const heroMap = {};
            const copyMap = {};
            activeList.forEach((c) => {
                if (c.hero_video_url || c.hero_poster_url) {
                    heroMap[c.code] = {
                        video: c.hero_video_url || null,
                        poster: c.hero_poster_url || null,
                    };
                }
                if (c.hero_copy && Object.keys(c.hero_copy).length) {
                    copyMap[c.code] = c.hero_copy;
                }
            });
            setHeroByCode(heroMap);
            setHeroCopyByCode(copyMap);

            // Snap the current country if it's inactive.
            const cached = localStorage.getItem(STORAGE_COUNTRY);
            let effective = country;
            if (cached && !active.has(cached)) {
                effective = pickFallback(active);
                localStorage.setItem(STORAGE_COUNTRY, effective);
                setCountryState(effective);
            }

            // First-time visitor: apply the geo hint IF the detected
            // country is active. Otherwise the API already returned a
            // safe default in `country`.
            if (geoRes?.data?.country && !cached) {
                const geoCode = geoRes.data.country;
                effective = active.has(geoCode) ? geoCode : pickFallback(active);
                localStorage.setItem(STORAGE_COUNTRY, effective);
                setCountryState(effective);
                if (!localStorage.getItem(STORAGE_LANG_MANUAL)) {
                    i18n.changeLanguage(defaultLanguageFor(effective));
                }
            }

            setReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    const setCountry = useCallback(
        (code) => {
            const next = COUNTRY_CODES.includes(code) ? code : DEFAULT_COUNTRY;
            setCountryState(next);
            localStorage.setItem(STORAGE_COUNTRY, next);
            const allowed = languagesForCountry(next);
            const currentLang = i18n.resolvedLanguage || i18n.language || "en";
            const manuallyPicked = !!localStorage.getItem(STORAGE_LANG_MANUAL);
            if (!allowed.includes(currentLang)) {
                // Current language isn't offered in the new market — snap
                // to that market's default even if the visitor had manually
                // picked one (their choice is invalid here). Clear the
                // manual flag so future country changes go back to auto.
                i18n.changeLanguage(defaultLanguageFor(next));
                localStorage.removeItem(STORAGE_LANG_MANUAL);
            } else if (!manuallyPicked) {
                i18n.changeLanguage(defaultLanguageFor(next));
            }
        },
        [i18n]
    );

    const setLanguage = useCallback(
        (lang) => {
            i18n.changeLanguage(lang);
            localStorage.setItem(STORAGE_LANG_MANUAL, "1");
        },
        [i18n]
    );

    // Public list shown in switchers = KNOWN countries ∩ ACTIVE codes.
    const visibleCountries = useMemo(() => {
        if (!activeCodes) return [getCountry(DEFAULT_COUNTRY)]; // pre-boot skeleton
        return COUNTRIES.filter((c) => activeCodes.has(c.code));
    }, [activeCodes]);

    const value = useMemo(
        () => ({
            country,
            countryInfo: getCountry(country),
            countries: visibleCountries,
            allCountries: COUNTRIES,
            setCountry,
            setLanguage,
            ready,
            // Bespoke hero video/poster URLs for the CURRENT country, or
            // null if admin hasn't uploaded one — Landing falls back to
            // the default asset in that case.
            heroVideoUrl: heroByCode[country]?.video || null,
            heroPosterUrl: heroByCode[country]?.poster || null,
            heroByCode,
            heroCopyByCode,
        }),
        [country, visibleCountries, setCountry, setLanguage, ready, heroByCode, heroCopyByCode]
    );

    return (
        <CountryContext.Provider value={value}>
            {children}
        </CountryContext.Provider>
    );
}

export function useCountry() {
    const ctx = useContext(CountryContext);
    if (!ctx) throw new Error("useCountry must be used inside <CountryProvider>");
    return ctx;
}

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function pickFallback(activeSet) {
    if (activeSet.has(DEFAULT_COUNTRY)) return DEFAULT_COUNTRY;
    // If HK is disabled (unlikely but possible), fall back to the first
    // active code in COUNTRIES order.
    for (const c of COUNTRIES) {
        if (activeSet.has(c.code)) return c.code;
    }
    return DEFAULT_COUNTRY;
}

function readInitialCountry() {
    const url = new URL(window.location.href);
    const q = (url.searchParams.get("country") || "").toUpperCase();
    if (COUNTRY_CODES.includes(q)) {
        localStorage.setItem(STORAGE_COUNTRY, q);
        return q;
    }
    const seg = url.pathname.split("/").filter(Boolean)[0]?.toUpperCase();
    if (seg && COUNTRY_CODES.includes(seg)) {
        localStorage.setItem(STORAGE_COUNTRY, seg);
        return seg;
    }
    const cached = localStorage.getItem(STORAGE_COUNTRY);
    if (cached && COUNTRY_CODES.includes(cached)) return cached;
    return DEFAULT_COUNTRY;
}
